/**
 * ExtractionGroupSection Component
 *
 * Collapsible group header for the Data Extraction tab.
 * Displays group name with status badges and expand/collapse toggle.
 */

import { ReactNode } from 'react';
import { TableRow, TableCell } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { ChevronDown, ChevronRight, Layers } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ExtractionDocumentGroup } from '@/types/extraction-grouping';

interface ExtractionGroupSectionProps<T> {
  /** The group data */
  group: ExtractionDocumentGroup<T>;
  /** Whether the group is expanded */
  isExpanded: boolean;
  /** Callback when the group header is clicked */
  onToggle: () => void;
  /** Number of columns in the table (for colspan) */
  colSpan: number;
  /** Render function for the group content (document rows) */
  renderContent: () => ReactNode;
}

export function ExtractionGroupSection<T>({
  group,
  isExpanded,
  onToggle,
  colSpan,
  renderContent
}: ExtractionGroupSectionProps<T>) {
  const ChevronIcon = isExpanded ? ChevronDown : ChevronRight;

  return (
    <>
      {/* Group Header Row */}
      <TableRow
        className={cn(
          'bg-muted/50 hover:bg-muted/70 cursor-pointer border-b transition-colors',
          isExpanded && 'bg-muted/60'
        )}
        onClick={onToggle}
      >
        <TableCell colSpan={colSpan} className="py-2 px-3">
          <div className="flex items-center gap-2">
            {/* Expand/Collapse Icon */}
            <ChevronIcon className="h-4 w-4 text-muted-foreground shrink-0" />

            {/* Group Icon */}
            <Layers className="h-4 w-4 text-blue-500 shrink-0" />

            {/* Group Name */}
            <span className="font-semibold text-sm">
              {group.displayName}
            </span>

            {/* Total count */}
            <span className="text-xs text-muted-foreground">
              ({group.totalCount})
            </span>

            {/* Status Badges */}
            <div className="flex gap-1.5 ml-auto">
              {group.completedCount > 0 && (
                <Badge
                  variant="secondary"
                  className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 text-xs px-1.5 py-0"
                >
                  {group.completedCount} completed
                </Badge>
              )}
              {group.processingCount > 0 && (
                <Badge
                  variant="secondary"
                  className="bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400 text-xs px-1.5 py-0"
                >
                  {group.processingCount} processing
                </Badge>
              )}
              {group.unprocessedCount > 0 && (
                <Badge
                  variant="secondary"
                  className="bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400 text-xs px-1.5 py-0"
                >
                  {group.unprocessedCount} unprocessed
                </Badge>
              )}
              {group.failedCount > 0 && (
                <Badge
                  variant="destructive"
                  className="text-xs px-1.5 py-0"
                >
                  {group.failedCount} failed
                </Badge>
              )}
            </div>
          </div>
        </TableCell>
      </TableRow>

      {/* Group Content (Document Rows) - Only shown when expanded */}
      {isExpanded && renderContent()}
    </>
  );
}
